import { apiClient } from './client';
import { API_ENDPOINTS } from './endpoints';

/**
 * Progress Sync Response
 * Returned after triggering manual sync
 */
export interface ProgressSyncResponse {
  success: boolean;
  message: string;
  activitiesSynced: number;
  daysAggregated: number;
  statsUpdated: boolean;
}

/**
 * Participant Stats
 * Aggregated stats for a participant in a challenge
 */
export interface ParticipantStats {
  challengeId: string;
  walletAddress: string;
  totalRunDistance: number; // In kilometers
  totalRunDuration: number; // In seconds
  totalRunCount: number; // Number of activities
  daysCompleted: number; // Days where goal was met
  daysLogged: number; // Days with any activity
  currentStreak: number; // Consecutive days
  longestStreak: number; // Longest consecutive days achieved
  lastActivityAt: string | null; // ISO date string
}

/**
 * Daily Progress Record
 * Individual day's progress for a participant
 */
export interface DailyProgress {
  date: string; // ISO date string
  runDistance: number; // In kilometers
  runDuration: number; // In seconds
  runCount: number; // Number of activities
  goalMet: boolean; // Whether daily goal was achieved
  percentageOfGoal: number; // How much of goal completed (0-100+)
}

/**
 * Recent Activity for display
 */
export interface RecentActivity {
  label: string; // "Today", "Yesterday"
  date: string; // ISO date string
  distance: number; // In kilometers
  goalMet: boolean;
}

/**
 * Enhanced Participant Stats
 * All-in-one stats for the Your Challenge Stats card
 * Matches backend response structure from /enhanced-stats endpoint
 */
export interface EnhancedParticipantStats {
  // Core stats (flat)
  totalRunDistance: number;
  totalRunDuration: number;
  totalRunCount: number;
  daysCompleted: number;
  daysLogged: number;
  currentStreak: number;
  longestStreak: number;
  lastActivityAt: string | null;

  // Computed fields (flat)
  averagePace: string | null;
  bestDay: { date: string; distance: number } | null;
  recentActivities: RecentActivity[];

  // Challenge context (nested)
  challengeContext: {
    id: string;
    name: string;
    type: string;
    dailyGoal: number;
    totalDistanceGoal: number;
    durationDays: number;
    requiredDays: number;
    startDate: string;
    endDate: string;
    status: string;
    currentDay: number;
    daysLeft: number;
    stakeAmount: number;
    totalPrizePool: number;
    userStakePaid: number;
    // Marathon-specific fields
    targetDurationSeconds: number | null;
    isOffChain: boolean;
  };
}

/**
 * Progress API
 * Handles progress tracking and syncing with Strava
 */
export const progressApi = {
  /**
   * Manually trigger progress sync for current user
   * Fetches activities from Strava and updates progress
   *
   * Rate limited: 5 syncs per 15 minutes per user
   *
   * @param challengeId - Challenge ID to sync progress for
   * @returns Sync result with counts of synced items
   */
  syncProgress: async (challengeId: string): Promise<ProgressSyncResponse> => {
    const response = await apiClient.post(API_ENDPOINTS.PROGRESS.SYNC(challengeId));
    return response.data.data;
  },

  /**
   * Get calculated stats for a participant
   *
   * @param challengeId - Challenge ID
   * @param walletAddress - Participant's wallet address
   * @returns Participant's aggregated stats
   */
  getParticipantStats: async (
    challengeId: string,
    walletAddress: string
  ): Promise<ParticipantStats> => {
    const response = await apiClient.get(
      API_ENDPOINTS.PROGRESS.GET_STATS(challengeId, walletAddress)
    );
    return response.data.data;
  },

  /**
   * Get daily progress records for a participant
   *
   * @param challengeId - Challenge ID
   * @param walletAddress - Participant's wallet address
   * @returns Array of daily progress records
   */
  getDailyProgress: async (
    challengeId: string,
    walletAddress: string
  ): Promise<DailyProgress[]> => {
    const response = await apiClient.get(
      API_ENDPOINTS.PROGRESS.GET_DAILY(challengeId, walletAddress)
    );
    return response.data.data;
  },

  /**
   * Get enhanced stats for Your Challenge Stats card
   * Single endpoint returning all needed data
   *
   * @param challengeId - Challenge ID
   * @param walletAddress - Participant's wallet address
   * @returns Enhanced stats with derived values and context
   */
  getEnhancedStats: async (
    challengeId: string,
    walletAddress: string
  ): Promise<EnhancedParticipantStats> => {
    const response = await apiClient.get(
      API_ENDPOINTS.PROGRESS.GET_ENHANCED_STATS(challengeId, walletAddress)
    );
    return response.data.data;
  },
};
